import type { PublicInvoice } from '@/types/public';

type PublicInvoiceStatus = PublicInvoice['status'];

const NORMALIZED_STATUSES: ReadonlySet<string> = new Set([
  'draft',
  'sent',
  'partial',
  'paid',
  'overdue',
  'void',
]);

const PAYABLE_STATUSES: ReadonlySet<PublicInvoiceStatus> = new Set(['sent', 'partial', 'overdue']);

export function normalizePublicInvoiceStatus(value: unknown): PublicInvoiceStatus {
  const status = String(value ?? 'draft').toLowerCase();
  if (status === 'partially_paid') return 'partial';
  if (status === 'viewed') return 'sent';
  if (status === 'open') return 'sent';
  if (status === 'written_off') return 'void';
  return NORMALIZED_STATUSES.has(status) ? (status as PublicInvoiceStatus) : 'draft';
}

export function isPublicInvoicePayable(status: PublicInvoiceStatus, balanceDue: number): boolean {
  return balanceDue > 0 && PAYABLE_STATUSES.has(status);
}

export function isPublicInvoicePaid(status: PublicInvoiceStatus, balanceDue: number): boolean {
  if (status === 'void') return false;
  return status === 'paid' || balanceDue <= 0;
}

/**
 * What the payer can pay online now. The server's `payableBalance` nets out
 * their own bank money still processing and is 0 while a card charge is being
 * verified. An older payload without it keeps the old rule: nothing while any
 * payment is in flight.
 */
export function publicPayableAmount(invoice: {
  payableBalance?: number | null;
  paymentAttemptSettling?: boolean;
  balanceDue: number;
}): number {
  if (typeof invoice.payableBalance === 'number' && Number.isFinite(invoice.payableBalance)) {
    return Math.max(0, invoice.payableBalance);
  }
  return invoice.paymentAttemptSettling === true ? 0 : invoice.balanceDue;
}
